import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/AppHeader";

const IMG = "https://images.unsplash.com/photo-1706855203772-c249b75fe016?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const Field = ({ label, ...p }) => (
  <label className="block">
    <span className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)]">{label}</span>
    <input {...p} required className="mt-2 w-full bg-[var(--card)] border border-[var(--line)] focus:border-[var(--cyan)] outline-none px-4 py-3 text-sm transition-colors" />
  </label>
);

const GoogleBtn = () => (
  <button type="button" data-testid="google-login-btn"
    onClick={() => {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const redirectUrl = window.location.origin + "/dashboard";
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    }}
    className="w-full flex items-center justify-center gap-3 py-3 bg-white text-[#0A0D14] font-semibold text-sm hover:bg-slate-100 transition-colors">
    <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
    Continue with Google
  </button>
);

export default function Login() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const next = () => { const n = localStorage.getItem("rd_next"); localStorage.removeItem("rd_next"); return n; };
  const registered = useRef(false);
  useEffect(() => {
    if (user) navigate(next() || (registered.current ? "/subscribe?start=1" : "/dashboard"), { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const body = mode === "login" ? { email: form.email, password: form.password } : form;
      const { data } = await api.post(`/auth/${mode === "login" ? "login" : "register"}`, body);
      registered.current = mode !== "login";
      setUser(data);
    } catch (e2) { setErr(errMsg(e2)); }
    setBusy(false);
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]" data-testid="login-page">
      <div className="relative hidden lg:block overflow-hidden">
        <img src={IMG} alt="Modern house" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[var(--bg)]/60" />
        <div className="absolute inset-0 blueprint-grid opacity-60" />
        <div className="absolute bottom-12 left-12 right-12">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[var(--cyan)]">Schematic design, accelerated</p>
          <p className="mt-4 text-3xl font-display font-bold max-w-md leading-tight">From square footage to a Rhino model before your coffee cools.</p>
        </div>
      </div>
      <div className="flex flex-col px-6 sm:px-12 py-8 blueprint-grid">
        <Logo />
        <div className="flex-1 flex items-center">
          <div className="w-full max-w-sm mx-auto rise">
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">{mode === "login" ? "Welcome back" : "Create your studio"}</h1>
            <p className="mt-3 text-sm text-[var(--muted)]">Sign in to draft, revise and export to Rhino.</p>
            <div className="mt-10 space-y-6">
              <GoogleBtn />
              <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-widest text-[var(--muted)]"><span className="flex-1 h-px bg-[var(--line)]" />or email<span className="flex-1 h-px bg-[var(--line)]" /></div>
              <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
                {mode === "register" && <Field label="Name" data-testid="register-name-input" value={form.name} onChange={set("name")} />}
                <Field label="Email" type="email" data-testid="auth-email-input" value={form.email} onChange={set("email")} />
                <Field label="Password" type="password" data-testid="auth-password-input" value={form.password} onChange={set("password")} />
                {err && <p className="text-sm text-red-400" data-testid="auth-error">{err}</p>}
                <button data-testid="auth-submit-btn" disabled={busy} className="w-full py-3 bg-[var(--cyan)] text-[var(--bg)] font-semibold font-display flex items-center justify-center gap-2 hover:brightness-110 transition-[filter] disabled:opacity-60">
                  {busy && <Loader2 size={16} className="animate-spin" />}{mode === "login" ? "Sign in" : "Create account"}
                </button>
              </form>
              <button data-testid="auth-mode-toggle" onClick={() => { setMode(mode === "login" ? "register" : "login"); setErr(""); }} className="text-sm text-[var(--muted)] hover:text-white transition-colors">
                {mode === "login" ? "New here? Create an account →" : "Already have an account? Sign in →"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
